import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { procedureSteps } from '@/graph/procedureSteps';
import { ProcedureStepType } from '@/types/ProcedureStep';

/** Display names for the three optional PACU timestamps. Values come from the procedure step `name`. */
export type PacuStepNames = {
  transferredToChair: string;
  transferredToPT: string;
  ptComplete: string;
};

export const DEFAULT_PACU_STEP_NAMES: PacuStepNames = {
  transferredToChair: 'Transferred to Chair',
  transferredToPT: 'Transferred to PT',
  ptComplete: 'PT Complete',
};

export type PacuOptionalProcedureSteps = {
  /** False while the enabled procedure-step list is still loading. */
  ready: boolean;
  transferredToChair: boolean;
  transferredToPT: boolean;
  ptComplete: boolean;
  names: PacuStepNames;
};

const hidden: PacuOptionalProcedureSteps = {
  ready: false,
  transferredToChair: false,
  transferredToPT: false,
  ptComplete: false,
  names: DEFAULT_PACU_STEP_NAMES,
};

const shown: PacuOptionalProcedureSteps = {
  ready: true,
  transferredToChair: true,
  transferredToPT: true,
  ptComplete: true,
  names: DEFAULT_PACU_STEP_NAMES,
};

export type PacuProcedureStepNameSource = {
  type: string;
  name?: string | null;
};

const nameForType = (type: string, name: string | null | undefined, names: PacuStepNames) => {
  const trimmed = name?.trim();
  if (!trimmed) return;
  if (type === ProcedureStepType.TransferredToChair) names.transferredToChair = trimmed;
  else if (type === ProcedureStepType.TransferredToPT) names.transferredToPT = trimmed;
  else if (type === ProcedureStepType.PTComplete) names.ptComplete = trimmed;
};

/** Enabled flags plus the hospital's procedure-step names for the three optional PACU timestamps. */
export const pacuOptionalStepsFromProcedureSteps = (
  steps: Iterable<PacuProcedureStepNameSource>
): PacuOptionalProcedureSteps => {
  const list = [...steps];
  const enabled = new Set(list.map(step => step.type));
  const names: PacuStepNames = { ...DEFAULT_PACU_STEP_NAMES };
  list.forEach(step => nameForType(step.type, step.name, names));
  return {
    ready: true,
    transferredToChair: enabled.has(ProcedureStepType.TransferredToChair),
    transferredToPT: enabled.has(ProcedureStepType.TransferredToPT),
    ptComplete: enabled.has(ProcedureStepType.PTComplete),
    names,
  };
};

export const pacuOptionalStepsFromTypes = (types: Iterable<string>): PacuOptionalProcedureSteps =>
  pacuOptionalStepsFromProcedureSteps([...types].map(type => ({ type })));

/**
 * A later optional PACU step can start when the previous optional step is turned off,
 * or when that previous step has already been completed.
 */
export const pacuStepPrerequisiteMet = (previousStepEnabled: boolean, previousStepCompleted: boolean): boolean =>
  !previousStepEnabled || previousStepCompleted;

type ProcedureStepsQuery = {
  procedureSteps?: PacuProcedureStepNameSource[] | null;
};

export const usePacuOptionalProcedureSteps = (): PacuOptionalProcedureSteps => {
  const { data, loading, error } = useQuery<ProcedureStepsQuery>(procedureSteps);
  const steps = data?.procedureSteps;

  return useMemo(() => {
    if (!steps) {
      return loading && !error ? hidden : shown;
    }
    return pacuOptionalStepsFromProcedureSteps(steps);
  }, [steps, loading, error]);
};
